import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import styledComponent from '../../../utils/styledComponent';
import theme from '../../../theme';
import SingleSelect from '../../../components/atoms/forms/SingleSelect';
import Button from '../../../components/atoms/forms/Button';
import Textarea from '../../../components/atoms/forms/Textarea';
import { MISSED_STOP_EXCLUSION_REASONS, requiresFreeText } from './reasons';

// One card per missed stop. Missed stops are included-for-reroute by
// default; the parent drives the card via the `decision` prop. Checked =
// included. The always-visible reason select and the Reroute checkbox are
// mutually exclusive: picking a reason excludes the stop (unchecking the
// box), re-checking the box clears the reason. Owns only the local
// in-progress picker state for an included stop.

const StyledDiv = styledComponent('div', {
  background: theme.colors.bgCard,
  border: `1px solid ${theme.colors.border}`,
  borderRadius: theme.radii.inner,
  padding: theme.spacing.bodyPadding,
  marginBottom: theme.spacing.fieldGap,

  '& .msc-header': {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    gap: theme.spacing.fieldGap,
  },
  '& .msc-info': {
    flex: 1,
    minWidth: 0,
  },
  '& .msc-title-row': {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing.gapMd,
    marginBottom: theme.spacing.labelGap,
  },
  '& .msc-title': {
    fontWeight: theme.fontWeights.semibold,
    color: theme.colors.textDark,
    fontSize: theme.typography.sectionHeader.fontSize,
  },
  '& .msc-meta': {
    fontSize: theme.typography.label.fontSize,
    color: theme.colors.textMuted,
  },

  '& .msc-add-toggle': {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing.inputLabelGap,
    fontSize: theme.typography.label.fontSize,
    cursor: 'pointer',
    userSelect: 'none',
  },

  '& .msc-picker': {
    marginTop: theme.spacing.fieldGap,
    padding: theme.spacing.bodyPadding,
    background: theme.colors.bgMuted,
    borderRadius: theme.radii.input,
    border: `1px solid ${theme.colors.borderLight}`,
  },
  '& .msc-field': {
    marginBottom: theme.spacing.fieldGap,
  },
  '& .msc-field:last-of-type': {
    marginBottom: 0,
  },
  '& .msc-label': {
    display: 'block',
    fontSize: theme.typography.hint.fontSize,
    fontWeight: theme.fontWeights.semibold,
    color: theme.colors.textMuted,
    textTransform: 'uppercase',
    letterSpacing: '0.5px',
    marginBottom: theme.spacing.inputLabelGap,
  },
  '& .msc-buttons': {
    display: 'flex',
    gap: theme.spacing.gapMd,
    justifyContent: 'flex-end',
    marginTop: theme.spacing.sectionGap,
    paddingTop: theme.spacing.fieldGap,
    borderTop: `1px solid ${theme.colors.borderLight}`,
  },
  '& .msc-summary': {
    marginTop: theme.spacing.fieldGap,
    padding: theme.spacing.bodyPadding,
    background: theme.colors.successBg,
    borderRadius: theme.radii.input,
    borderLeft: `3px solid ${theme.colors.primary}`,
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    gap: theme.spacing.fieldGap,
  },
  '& .msc-summary-text': {
    fontSize: theme.typography.label.fontSize,
    color: theme.colors.textDark,
  },
  '& .msc-summary-actions': {
    display: 'flex',
    gap: theme.spacing.gapMd,
    alignItems: 'center',
  },
  '& .msc-mode-group': {
    marginBottom: theme.spacing.fieldGap,
  },
  '& .msc-mode-label': {
    display: 'block',
    fontSize: theme.typography.hint.fontSize,
    fontWeight: theme.fontWeights.semibold,
    color: theme.colors.textMuted,
    textTransform: 'uppercase',
    letterSpacing: '0.5px',
    marginBottom: theme.spacing.inputLabelGap,
  },
  '& .msc-mode-option': {
    display: 'flex',
    alignItems: 'flex-start',
    gap: theme.spacing.inputLabelGap,
    padding: theme.spacing.labelGap,
    fontSize: theme.typography.label.fontSize,
    cursor: 'pointer',
  },
  '& .msc-mode-option input[type="radio"]': {
    marginTop: theme.spacing.labelGap,
    cursor: 'pointer',
    accentColor: theme.colors.primary,
  },
  '& .msc-mode-option-detail': {
    color: theme.colors.textMuted,
  },
  '& .msc-auto-callout': {
    padding: theme.spacing.bodyPadding,
    background: theme.colors.bgMuted,
    borderLeft: `3px solid ${theme.colors.primary}`,
    borderRadius: theme.radii.input,
    fontSize: theme.typography.label.fontSize,
    color: theme.colors.textDark,
    marginBottom: theme.spacing.fieldGap,
  },
  '& .msc-summary-auto': {
    background: theme.colors.bgMuted,
    borderLeft: `3px solid ${theme.colors.primary}`,
  },
  '& .msc-decision': {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing.labelGap,
    minWidth: theme.sizes.decisionColumnMinWidth,
  },
  '& .msc-reason-hint': {
    fontSize: theme.typography.hint.fontSize,
    color: theme.colors.danger,
    fontWeight: theme.fontWeights.semibold,
  },
  '& .msc-reason-notes': {
    marginTop: theme.spacing.labelGap,
  },
});

function MissedStopCard({
  missedStop,
  rowKey,
  todayRoutes,
  decision,
  onConfirm,
  onExclude,
  onInclude,
  onGoToRoute,
  allowSpecificAssignment,
}) {
  const isIncluded = Boolean(decision) && decision.status === 'included';
  const isExcluded = Boolean(decision) && decision.status === 'excluded';

  const [isPickerOpen, setIsPickerOpen] = useState(false);
  const [draftRoute, setDraftRoute] = useState(
    isIncluded && decision.targetRouteName ? decision.targetRouteName : '',
  );
  const [draftMode, setDraftMode] = useState(
    isIncluded ? decision.mode || 'auto' : 'auto',
  );

  // Keep the picker draft in sync if the decision changes underneath us
  // (e.g. parent re-includes an excluded stop as auto).
  useEffect(() => {
    if (isIncluded) {
      setDraftRoute(decision.targetRouteName || '');
      setDraftMode(decision.mode || 'auto');
    }
  }, [isIncluded, decision]);

  const canConfirmSpecific = draftRoute !== '';

  const buildAssignmentPayload = (mode) => ({
    mode,
    targetRouteName: mode === 'specific' ? draftRoute : null,
    stopId: missedStop.id || '',
    originalRoute: missedStop.route || '',
    originalDate: missedStop.stockingDate || '',
    toteCountPerStop:
      typeof missedStop.toteCountPerStop === 'number'
        ? missedStop.toteCountPerStop
        : null,
  });

  const handleToggle = (checked) => {
    if (checked) {
      onInclude();
    } else {
      setIsPickerOpen(false);
      onExclude('', '');
    }
  };

  const handleConfirmAuto = () => {
    onConfirm(buildAssignmentPayload('auto'));
    setIsPickerOpen(false);
  };

  const handleConfirmSpecific = () => {
    if (!canConfirmSpecific) return;
    onConfirm(buildAssignmentPayload('specific'));
    setIsPickerOpen(false);
  };

  const handleCancelPicker = () => {
    setDraftRoute(isIncluded ? decision.targetRouteName || '' : '');
    setDraftMode(isIncluded ? decision.mode || 'auto' : 'auto');
    setIsPickerOpen(false);
  };

  const handleEdit = () => {
    setDraftRoute(isIncluded ? decision.targetRouteName || '' : '');
    setDraftMode(isIncluded ? decision.mode || 'auto' : 'auto');
    setIsPickerOpen(true);
  };

  const handleGoToRoute = () => {
    if (isIncluded && decision.targetRouteName) {
      onGoToRoute(decision.targetRouteName);
    }
  };

  const showPicker = isIncluded && isPickerOpen;
  const showIncludedSummary = isIncluded && !isPickerOpen;
  const isAutoSummary =
    showIncludedSummary && (decision.mode || 'auto') === 'auto';

  const routeOpts = todayRoutes.map((r) => ({
    value: r.routeName,
    label: `${r.routeName} (${r.stops.length} stops)`,
  }));

  return (
    <StyledDiv data-testid={`missed-stop-card-${rowKey}`}>
      <div className="msc-header">
        <div className="msc-info">
          <div className="msc-title-row">
            <span className="msc-title">{missedStop.id}</span>
          </div>
          <div className="msc-meta">
            <strong>Original Route:</strong> {missedStop.route || '—'}
            {' · '}
            <strong>Original Date:</strong> {missedStop.stockingDate || '—'}
          </div>
        </div>
        <div className="msc-decision">
          <label className="msc-add-toggle">
            <input
              type="checkbox"
              checked={isIncluded}
              onChange={(e) => handleToggle(e.target.checked)}
              data-testid={`missed-stop-toggle-${rowKey}`}
            />
            Reroute
          </label>
          <SingleSelect
            inputId={`missed-stop-reason-${rowKey}`}
            options={MISSED_STOP_EXCLUSION_REASONS}
            value={
              isExcluded && decision.reasonCode
                ? MISSED_STOP_EXCLUSION_REASONS.find(
                    (r) => r.value === decision.reasonCode,
                  ) || null
                : null
            }
            onChange={(opt) => (opt ? onExclude(opt.value, '') : onInclude())}
            placeholder="Deselect reason…"
            isClearable
            data-testid={`missed-stop-reason-${rowKey}`}
          />
          {isExcluded && !decision.reasonCode && (
            <div
              className="msc-reason-hint"
              data-testid={`missed-stop-reason-hint-${rowKey}`}
            >
              Reason required — pick why this stop shouldn’t reroute.
            </div>
          )}
          {isExcluded && requiresFreeText(decision.reasonCode) && (
            <div className="msc-reason-notes">
              <Textarea
                id={`missed-stop-reason-notes-${rowKey}`}
                rows={2}
                placeholder="Explain the reason (required)"
                value={decision.notes || ''}
                onChange={(e) => onExclude(decision.reasonCode, e.target.value)}
                dataTestId={`missed-stop-reason-notes-${rowKey}`}
              />
            </div>
          )}
        </div>
      </div>

      {showPicker && (
        <div
          className="msc-picker"
          data-testid={`missed-stop-picker-${rowKey}`}
        >
          <div className="msc-mode-group">
            <span className="msc-mode-label">
              How should this stop be added?
            </span>
            <label className="msc-mode-option">
              <input
                type="radio"
                name={`missed-stop-mode-${rowKey}`}
                value="auto"
                checked={draftMode === 'auto'}
                onChange={() => setDraftMode('auto')}
                data-testid={`missed-stop-mode-auto-${rowKey}`}
              />
              <span>
                <strong>Auto-assign via Nextmv</strong>{' '}
                <span className="msc-mode-option-detail">
                  — let the optimizer place this stop on the best route
                  (recommended)
                </span>
              </span>
            </label>
            {allowSpecificAssignment && (
              <label className="msc-mode-option">
                <input
                  type="radio"
                  name={`missed-stop-mode-${rowKey}`}
                  value="specific"
                  checked={draftMode === 'specific'}
                  onChange={() => setDraftMode('specific')}
                  data-testid={`missed-stop-mode-specific-${rowKey}`}
                />
                <span>
                  <strong>Choose a specific route</strong>{' '}
                  <span className="msc-mode-option-detail">
                    — manually pick the route
                  </span>
                </span>
              </label>
            )}
          </div>

          {draftMode === 'auto' ? (
            <>
              <div
                className="msc-auto-callout"
                data-testid={`missed-stop-auto-callout-${rowKey}`}
              >
                ⚡ No route needed. This stop will be queued for re-optimization
                — when you run Nextmv for the selected FC, date, and shift,
                it&apos;ll be inserted into the most efficient route
                automatically.
              </div>
              <div className="msc-buttons">
                <Button
                  variant="secondary"
                  onClick={handleCancelPicker}
                  dataTestId={`missed-stop-cancel-picker-${rowKey}`}
                >
                  Cancel
                </Button>
                <Button
                  variant="primary"
                  onClick={handleConfirmAuto}
                  dataTestId={`missed-stop-auto-confirm-${rowKey}`}
                >
                  Add to Re-optimization
                </Button>
              </div>
            </>
          ) : (
            <>
              <div className="msc-field">
                <span className="msc-label">Select Route</span>
                <SingleSelect
                  inputId={`route-${rowKey}`}
                  options={routeOpts}
                  value={routeOpts.find((o) => o.value === draftRoute) || null}
                  onChange={(opt) => setDraftRoute(opt ? opt.value : '')}
                  placeholder="— Select a route —"
                  isClearable
                  data-testid={`missed-stop-route-${rowKey}`}
                />
              </div>
              <div className="msc-buttons">
                <Button
                  variant="secondary"
                  onClick={handleCancelPicker}
                  dataTestId={`missed-stop-cancel-picker-${rowKey}`}
                >
                  Cancel
                </Button>
                <Button
                  variant="primary"
                  onClick={handleConfirmSpecific}
                  disabled={!canConfirmSpecific}
                  dataTestId={`missed-stop-confirm-${rowKey}`}
                >
                  Confirm Add
                </Button>
              </div>
            </>
          )}
        </div>
      )}

      {showIncludedSummary && (
        <div
          className={`msc-summary${isAutoSummary ? ' msc-summary-auto' : ''}`}
          data-testid={`missed-stop-summary-${rowKey}`}
        >
          <span className="msc-summary-text">
            {isAutoSummary ? (
              <>⚡ Queued for re-optimization · Nextmv will pick the route</>
            ) : (
              <>
                ✓ Will be added to <strong>{decision.targetRouteName}</strong>
              </>
            )}
          </span>
          <div className="msc-summary-actions">
            <Button
              variant="link-primary"
              onClick={handleEdit}
              dataTestId={`missed-stop-edit-${rowKey}`}
            >
              Edit
            </Button>
            {/* Primary call-to-action — once the assignment is confirmed,
                scroll the supervisor straight to the target route card so
                they can see the staged ghost row in context. */}
            {!isAutoSummary && (
              <Button
                variant="primary"
                onClick={handleGoToRoute}
                dataTestId={`missed-stop-go-${rowKey}`}
              >
                Go to Route Editor →
              </Button>
            )}
          </div>
        </div>
      )}
    </StyledDiv>
  );
}

MissedStopCard.propTypes = {
  missedStop: PropTypes.shape({
    id: PropTypes.string,
    route: PropTypes.string,
    stockingIndex: PropTypes.number,
    stockingDate: PropTypes.string,
    toteCountPerStop: PropTypes.number,
  }).isRequired,
  rowKey: PropTypes.string.isRequired,
  todayRoutes: PropTypes.arrayOf(
    PropTypes.shape({
      routeName: PropTypes.string.isRequired,
      stops: PropTypes.array.isRequired,
    }),
  ).isRequired,
  decision: PropTypes.shape({
    status: PropTypes.oneOf(['included', 'excluded']),
    mode: PropTypes.oneOf(['auto', 'specific']),
    targetRouteName: PropTypes.string,
    reasonCode: PropTypes.string,
    notes: PropTypes.string,
  }),
  onConfirm: PropTypes.func.isRequired,
  onExclude: PropTypes.func.isRequired,
  onInclude: PropTypes.func.isRequired,
  onGoToRoute: PropTypes.func,
  allowSpecificAssignment: PropTypes.bool,
};

MissedStopCard.defaultProps = {
  decision: null,
  onGoToRoute: () => {},
  allowSpecificAssignment: true,
};

export default MissedStopCard;
